import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useCurrency } from '@kss/currency';
import { cn } from '../lib/cn';
import { applyGuard } from '../validation/sanitize';
import { Button } from '../primitives/Button';
import { VegMark } from '../primitives/VegMark';
import { QtyStepper } from '../forms/QtyStepper';
import { PriceTag } from './PriceTag';

export interface ItemDetailItem {
  id: number;
  name: string;
  desc: string;
  price: number;
  mrp?: number;
  /** Undefined when the dish isn't in the menu we hold — render no mark rather than
   *  guessing. Defaulting this to `true` mislabels non-veg food as vegetarian. */
  veg?: boolean;
  image: string;
}

/**
 * Item detail popup: shows the dish and lets the customer add a note for the
 * kitchen + choose a quantity before adding to cart. Controlled by `item`
 * (null = closed). `onAdd(qty, note)` receives the choices.
 *
 * In `readOnly` mode it's a look-only detail (used for an already-placed order
 * line): no note / quantity, and the action button is shown disabled with an
 * optional `disabledHint` (e.g. "unlocks after your bill is approved").
 */
export function ItemDetailModal({
  item,
  onClose,
  onAdd,
  readOnly = false,
  actionLabel,
  actionDisabled = false,
  disabledHint,
  onAction
}: {
  item: ItemDetailItem | null;
  onClose: () => void;
  onAdd: (qty: number, note: string) => void;
  readOnly?: boolean;
  actionLabel?: string;
  actionDisabled?: boolean;
  disabledHint?: string;
  /** readOnly action when enabled (defaults to closing the sheet). */
  onAction?: () => void;
}) {
  const { format } = useCurrency();
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');

  // Fresh quantity + note each time a different item opens.
  useEffect(() => {
    if (item) {
      setQty(1);
      setNote('');
    }
  }, [item?.id]);

  const open = Boolean(item);

  return (
    <div
      className={cn(
        'fixed inset-0 z-[95] flex items-end justify-center transition-opacity duration-200 sm:items-center',
        open ? 'opacity-100' : 'pointer-events-none opacity-0'
      )}
      aria-hidden={!open}
    >
      <div className='absolute inset-0 bg-black/40' onClick={onClose} />

      {item && (
        <div
          role='dialog'
          aria-label={item.name}
          className='relative z-10 w-full max-w-[420px] overflow-hidden rounded-t-2xl bg-surface-card shadow-pop sm:rounded-2xl'
        >
          <div
            className='relative h-44 w-full'
            style={{
              backgroundImage: `url(${item.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <button
              onClick={onClose}
              aria-label='Close'
              className='absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-surface-card/90 text-content-secondary hover:bg-surface-card'
            >
              <X size={16} />
            </button>
          </div>

          <div className='p-4'>
            <div className='flex items-center gap-1.5 font-display text-[16px] font-bold'>
              <VegMark veg={item.veg} bare /> {item.name}
            </div>
            <div className='mt-1 text-[12px] text-content-secondary'>{item.desc}</div>
            <div className='mt-2'>
              <PriceTag price={item.price} mrp={item.mrp} />
            </div>

            {readOnly ? (
              /* Look-only detail for an already-placed line: action stays disabled. */
              <div className='mt-4'>
                <Button
                  block
                  size='lg'
                  disabled={actionDisabled}
                  onClick={actionDisabled ? undefined : (onAction ?? onClose)}
                >
                  {actionLabel ?? 'Place order'}
                </Button>
                {disabledHint && (
                  <div className='mt-2 text-center text-[11px] text-content-muted'>{disabledHint}</div>
                )}
              </div>
            ) : (
              <>
                {/* Kitchen note */}
                <label className='mt-4 block text-[11px] font-bold uppercase tracking-wider text-content-muted'>
                  Note for the kitchen
                </label>
                <textarea
                  value={note}
                  // The note is printed on the KOT the kitchen reads, so it is shaped on the way
                  // in: markup stripped, runs of spaces collapsed, capped whether typed or pasted.
                  onChange={(e) => setNote(applyGuard(e.target.value, 'text', 140))}
                  rows={2}
                  maxLength={140}
                  placeholder='e.g. less spicy, no onion, extra gravy…'
                  className='mt-1.5 w-full resize-none rounded-lg border border-border bg-surface-card px-3 py-2 text-[12.5px] text-content-primary placeholder:text-content-muted focus:border-primary focus:outline-none'
                />
                <div className='mt-0.5 text-right text-[10px] text-content-muted'>{note.length}/140</div>

                <div className='mt-3 flex items-center gap-3'>
                  <QtyStepper qty={qty} onInc={() => setQty((q) => q + 1)} onDec={() => setQty((q) => Math.max(1, q - 1))} />
                  <Button
                    block
                    size='lg'
                    onClick={() => {
                      onAdd(qty, note.trim());
                      onClose();
                    }}
                  >
                    Add {format(item.price * qty)}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
